import React, { useLayoutEffect, useReducer, useRef } from 'react';
import { Modal } from 'react-bootstrap';

const BASE_Z_INDEX = 1050;
const Z_INDEX_STEP = 20;

let openModalCount = 0;

export function getStackTopZIndex(extra = 10) {
  return BASE_Z_INDEX + openModalCount * Z_INDEX_STEP + extra;
}

function StackedModal({
  show,
  children,
  style,
  enforceFocus,
  restoreFocus,
  backdropClassName,
  ...props
}) {
  const stackLevelRef = useRef(0);
  const [, rerender] = useReducer((value) => value + 1, 0);

  useLayoutEffect(() => {
    if (!show) {
      if (stackLevelRef.current > 0) {
        openModalCount = Math.max(0, openModalCount - 1);
        stackLevelRef.current = 0;
        rerender();
      }
      return undefined;
    }

    if (stackLevelRef.current === 0) {
      openModalCount += 1;
      stackLevelRef.current = openModalCount;
      rerender();
    }

    return () => {
      if (stackLevelRef.current > 0) {
        openModalCount = Math.max(0, openModalCount - 1);
        stackLevelRef.current = 0;
      }
    };
  }, [show]);

  const stackLevel = stackLevelRef.current;
  const zIndex = BASE_Z_INDEX + stackLevel * Z_INDEX_STEP;
  const isNested = stackLevel > 1;

  useLayoutEffect(() => {
    if (!show || stackLevel === 0) return undefined;

    const backdropZIndex = zIndex - Z_INDEX_STEP / 2;

    const syncBackdrop = () => {
      const backdrops = document.querySelectorAll('.modal-backdrop');
      const backdrop = backdrops[stackLevel - 1];
      if (backdrop) {
        backdrop.style.zIndex = String(backdropZIndex);
      }
    };

    syncBackdrop();
    const raf = requestAnimationFrame(syncBackdrop);
    return () => cancelAnimationFrame(raf);
  }, [show, stackLevel, zIndex]);

  const mergedBackdropClassName = [
    backdropClassName,
    isNested ? 'modal-backdrop-stacked' : '',
  ].filter(Boolean).join(' ') || undefined;

  return (
    <Modal
      show={show}
      enforceFocus={enforceFocus !== undefined ? enforceFocus : !isNested}
      restoreFocus={restoreFocus !== undefined ? restoreFocus : !isNested}
      backdropClassName={mergedBackdropClassName}
      style={{ ...style, zIndex: show ? zIndex : undefined }}
      {...props}
    >
      {children}
    </Modal>
  );
}

StackedModal.Header = Modal.Header;
StackedModal.Body = Modal.Body;
StackedModal.Footer = Modal.Footer;
StackedModal.Title = Modal.Title;
StackedModal.Dialog = Modal.Dialog;

export default StackedModal;
